import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Film, Check, AlertTriangle, Video, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function defaultEnd() {
  const d = new Date(Date.now() + 7 * 86400000);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function NewReel() {
  const [products, setProducts] = useState([]);
  const [existingCampaignProductIds, setExistingCampaignProductIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState([]);
  const [videoOverrides, setVideoOverrides] = useState({}); // {productId: customVideoUrl}
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [winnersCount, setWinnersCount] = useState(2);
  const [endDate, setEndDate] = useState(defaultEnd());
  const [preOrderPrice, setPreOrderPrice] = useState("");
  const [estimatedShipDate, setEstimatedShipDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [videoOnly, setVideoOnly] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.listProducts(), api.listCampaigns()]).then(([prods, camps]) => {
      setProducts(prods);
      setExistingCampaignProductIds(new Set(camps.map((c) => c.productId)));
    });
  }, []);

  const toggle = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const setVideoOverride = (pid, url) => {
    setVideoOverrides((prev) => ({ ...prev, [pid]: url }));
  };

  // A product is "reel-ready" if it has Shopify video OR a manual override
  const isReelReady = (p) => Boolean(p.videoUrl) || Boolean((videoOverrides[p.id] || "").trim());

  // Base eligibility: no existing campaign for the product
  const baseEligible = products.filter((p) => !existingCampaignProductIds.has(p.id));
  // With Shopify video (auto-detected) — reel-ready without any manual override
  const withVideoCount = baseEligible.filter((p) => Boolean(p.videoUrl)).length;
  // Apply visible filters (search + videoOnly toggle)
  const q = searchQuery.trim().toLowerCase();
  const eligible = baseEligible.filter((p) => {
    if (videoOnly && !p.videoUrl) return false;
    if (!q) return true;
    return (p.title || "").toLowerCase().includes(q) || (p.vendor || "").toLowerCase().includes(q);
  });
  const selectedProducts = baseEligible.filter((p) => selectedIds.includes(p.id));
  const missingVideo = selectedProducts.filter((p) => !isReelReady(p));

  const submit = async (e) => {
    e.preventDefault();
    if (selectedIds.length < 2) {
      toast.error("Pick at least 2 products to compete");
      return;
    }
    if (winnersCount >= selectedIds.length) {
      toast.error("Winners must be fewer than the number of competing videos");
      return;
    }
    if (missingVideo.length > 0) {
      toast.error(`Add a video for: ${missingVideo.map((p) => p.title).join(", ")}`);
      return;
    }
    if (new Date(endDate).getTime() <= Date.now()) {
      toast.error("End date must be in the future");
      return;
    }
    setSubmitting(true);
    try {
      const isoEnd = new Date(endDate).toISOString();
      const cleanOverrides = {};
      Object.entries(videoOverrides).forEach(([pid, url]) => {
        const t = (url || "").trim();
        if (t && selectedIds.includes(pid)) cleanOverrides[pid] = t;
      });
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        winnersCount: Number(winnersCount),
        endDate: isoEnd,
        productIds: selectedIds,
        preOrderPrice: preOrderPrice ? Number(preOrderPrice) : null,
        estimatedShipDate: estimatedShipDate || null,
        productVideoUrls: cleanOverrides,
      };
      const r = await api.createReel(payload);
      toast.success("Reel created");
      navigate(`/app/reels/${r.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create reel");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="new-reel-page">
      <div>
        <Link to="/app/reels" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft size={14} /> Back to reels
        </Link>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-3 flex items-center gap-3">
          Create reel
          <Film size={28} className="text-gray-400" />
        </h1>
        <p className="text-sm text-gray-600 mt-2 max-w-xl">
          A vertical video competition. Customers swipe through, tap the heart to vote. Best with 9:16 portrait videos.
        </p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="lp-card p-6">
            <h2 className="text-base font-semibold tracking-tight mb-1">Reel details</h2>
            <p className="text-xs text-gray-500 mb-5">Name your reel and set when voting closes.</p>
            <div className="space-y-5">
              <div>
                <label className="lp-label" htmlFor="reel-name">Reel name</label>
                <input
                  id="reel-name"
                  className="lp-input"
                  data-testid="reel-name-input"
                  placeholder="Summer drop reel"
                  required
                  maxLength={80}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label className="lp-label" htmlFor="reel-desc">Description (optional)</label>
                <textarea
                  id="reel-desc"
                  className="lp-input"
                  data-testid="reel-desc-input"
                  rows={2}
                  placeholder="Pick which 2 styles join our summer drop."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="lp-label" htmlFor="reel-winners">Winners</label>
                  <input
                    id="reel-winners"
                    type="number"
                    min="1"
                    className="lp-input"
                    data-testid="reel-winners-input"
                    value={winnersCount}
                    onChange={(e) => setWinnersCount(e.target.value)}
                    required
                  />
                  <div className="lp-help">Top-voted videos that flip to pre-order.</div>
                </div>
                <div>
                  <label className="lp-label" htmlFor="reel-end">Ends on</label>
                  <input
                    id="reel-end"
                    type="datetime-local"
                    className="lp-input"
                    data-testid="reel-end-input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="lp-card p-6">
            <h2 className="text-base font-semibold tracking-tight mb-1">Pick products & video</h2>
            <p className="text-xs text-gray-500 mb-4">
              Each product needs a video. We auto-detect Shopify product videos —
              paste a YouTube/Vimeo/MP4 link to override or add one for products without a Shopify video.
            </p>

            {/* Filters */}
            <div className="mb-4 flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <input
                  type="search"
                  className="lp-input pl-9 text-sm"
                  placeholder="Search by product title or vendor…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="reel-product-search"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">🔍</div>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-[#111] cursor-pointer select-none" data-testid="reel-video-only-toggle">
                <input
                  type="checkbox"
                  checked={videoOnly}
                  onChange={(e) => setVideoOnly(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Video size={14} className="text-emerald-700" />
                Only show products with Shopify video
                <span className="text-xs text-gray-500">({withVideoCount})</span>
              </label>
            </div>

            {baseEligible.length === 0 ? (
              <div className="p-6 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
                <AlertTriangle size={18} className="text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-900">
                  Every product already has a campaign. Delete an existing campaign first.
                </div>
              </div>
            ) : eligible.length === 0 ? (
              <div className="p-6 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-600">
                No products match your filters.
                {videoOnly && (
                  <>
                    {" "}Only <strong>{withVideoCount}</strong> product{withVideoCount === 1 ? " has" : "s have"} a Shopify video.
                    {" "}
                    <button type="button" onClick={() => setVideoOnly(false)} className="underline text-[#d0473e]">
                      Show all products
                    </button> and paste a video URL for the ones you want.
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="text-xs text-gray-500 mb-3">
                  Showing <strong>{eligible.length}</strong> of {baseEligible.length} eligible products
                  {videoOnly && <> (video-ready only)</>}
                </div>
                <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1 -mr-1" data-testid="reel-product-list">
                  {eligible.map((p) => {
                  const checked = selectedIds.includes(p.id);
                  const hasShopifyVideo = Boolean(p.videoUrl);
                  const overrideUrl = videoOverrides[p.id] || "";
                  return (
                    <div
                      key={p.id}
                      className={`border rounded-xl transition-all ${
                        checked ? "border-gray-900 bg-gray-50/50" : "border-gray-200"
                      }`}
                      data-testid={`reel-product-${p.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggle(p.id)}
                        className="w-full text-left p-3 flex items-center gap-3"
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                          checked ? "border-gray-900 bg-gray-900" : "border-gray-300"
                        }`}>
                          {checked && <Check size={12} className="text-white" />}
                        </div>
                        <img src={p.imageUrl} alt="" className="w-12 h-12 object-cover rounded-lg bg-gray-100" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{p.title}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                            <span>${p.priceRange.min.toFixed(2)}</span>
                            {hasShopifyVideo ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700">
                                <Video size={11} /> Shopify video
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-amber-700">
                                <ImageIcon size={11} /> No video — paste URL below
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                      {checked && (
                        <div className="px-3 pb-3 pl-11">
                          <label className="lp-label text-[11px]" htmlFor={`override-${p.id}`}>
                            Video URL {hasShopifyVideo ? "(override)" : "(required)"}
                          </label>
                          <input
                            id={`override-${p.id}`}
                            type="url"
                            placeholder={hasShopifyVideo ? "Leave blank to use Shopify video" : "https://youtu.be/… or https://…/video.mp4"}
                            className="lp-input text-sm"
                            data-testid={`reel-override-${p.id}`}
                            value={overrideUrl}
                            onChange={(e) => setVideoOverride(p.id, e.target.value)}
                          />
                          {!hasShopifyVideo && !overrideUrl.trim() && (
                            <div className="text-[11px] text-amber-700 mt-1.5 flex items-center gap-1">
                              <AlertTriangle size={11} /> This product needs a video before the reel can launch
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </>
            )}
          </section>

          <section className="lp-card p-6">
            <h2 className="text-base font-semibold tracking-tight mb-1">Pre-order settings for winners</h2>
            <p className="text-xs text-gray-500 mb-5">Applied to whichever products win.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="lp-label" htmlFor="reel-price">Pre-order price ($, optional)</label>
                <input
                  id="reel-price"
                  type="number"
                  step="0.01"
                  className="lp-input"
                  data-testid="reel-price-input"
                  value={preOrderPrice}
                  onChange={(e) => setPreOrderPrice(e.target.value)}
                  placeholder="Defaults to product price"
                />
              </div>
              <div>
                <label className="lp-label" htmlFor="reel-ship">Estimated ship date</label>
                <input
                  id="reel-ship"
                  type="date"
                  className="lp-input"
                  data-testid="reel-ship-input"
                  value={estimatedShipDate}
                  onChange={(e) => setEstimatedShipDate(e.target.value)}
                />
              </div>
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <div className="lp-card p-6 sticky top-6">
            <div className="lp-section-label">Summary</div>
            <h3 className="text-lg font-medium tracking-tight mt-2">{name || "Untitled reel"}</h3>
            <div className="mt-5 space-y-3 text-sm">
              <Row label="Competing" value={`${selectedIds.length} video${selectedIds.length === 1 ? "" : "s"}`} />
              <Row label="Winners" value={`Top ${winnersCount}`} />
              <Row label="Ends" value={endDate ? new Date(endDate).toLocaleString() : "—"} />
              {missingVideo.length > 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  <span>Missing video on {missingVideo.length} product{missingVideo.length === 1 ? "" : "s"}</span>
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={submitting || selectedIds.length < 2 || missingVideo.length > 0}
              className="lp-btn-primary w-full justify-center mt-6"
              data-testid="submit-reel-btn"
            >
              {submitting ? "Creating…" : (<><Film size={16} /> Launch reel</>)}
            </button>
            <div className="text-[11px] text-gray-500 mt-3 text-center">
              Best video format: 9:16 vertical, under 60 seconds, MP4 or YouTube.
            </div>
          </div>
        </aside>
      </form>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium text-gray-900 text-right ml-3 truncate max-w-[60%]">{value}</span>
  </div>
);
